<template>
  <v-container grid-list-xl>
    <alert-error :error="error" />

    <v-card>
      <v-card-title>{{ $t('NOTIFICATIONS.GENERAL_INFORMATION') }}</v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-layout wrap>
          <v-flex md8 xs12>
            <v-card v-if="languages">
              <v-tabs v-model="tab">
                <v-tab v-for="language in languages" :key="language.id">
                  {{ language.name }}
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="tab">
                <v-tab-item v-for="language in languages" :key="language.id">
                  <v-card flat :disabled="readonly">
                    <v-card-text>
                      <v-row>
                        <v-col cols="12">
                          <v-text-field
                            v-if="language.locale == 'de'"
                            v-model="
                              localItem.translations[language.locale + 'Title']
                            "
                            :rules="rules.requiredTextLine65"
                            :counter="65"
                            :label="`${$t('ITEM.TITLE')} *`"
                          ></v-text-field>

                          <v-text-field
                            v-else
                            v-model="
                              localItem.translations[language.locale + 'Title']
                            "
                            :rules="rules.textLine65"
                            :counter="65"
                            :label="`${$t('ITEM.TITLE')}`"
                          ></v-text-field>
                        </v-col>

                        <v-col cols="12">
                          <v-ckeditor
                            v-if="language.locale === 'de'"
                            v-model="
                              localItem.translations[
                                language.locale + 'MainInformation'
                              ]
                            "
                            :rules="[rules.notEmpty]"
                            :editor="editor"
                            :config="editorConfig"
                            :label="`${$t('NOTIFICATIONS.MAIN_INFORMATION')} *`"
                          />

                          <v-ckeditor
                            v-else
                            v-model="
                              localItem.translations[
                                language.locale + 'MainInformation'
                              ]
                            "
                            :editor="editor"
                            :config="editorConfig"
                            :label="$t('NOTIFICATIONS.MAIN_INFORMATION')"
                          />
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
              </v-tabs-items>
            </v-card>
          </v-flex>

          <v-flex md4 xs12>
            <v-flex md12 xs12>
              <div v-if="!readonly && showChooseTime">
                {{ $t('NOTIFICATIONS.NOTIFICATION_WILL_BE_SEND_IMMEDIATELY') }}
                <v-switch
                  v-model="localItem.chooseTime"
                  :label="`${$t('NOTIFICATIONS.CHOOSE_DATETIME')}`"
                ></v-switch>
              </div>

              <div v-if="localItem.chooseTime">
                <v-text-field
                  v-model="dispatchTime"
                  v-mask="'##.##.#### ##:##'"
                  :disabled="readonly"
                  hint="'dd.mm.yyyy hh:mm'"
                  :label="`${$t('NOTIFICATIONS.SENDING_TIME')} *`"
                  placeholder="dd.mm.yyyy hh:mm"
                  :clearable="!readonly"
                  :rules="[validateDispatchTime()]"
                  :readonly="readonly"
                  @click:clear="clearDispatchTime"
                >
                </v-text-field>

                <p v-if="diffTimeZones">
                  {{ $t('NOTIFICATIONS.TIME_IN_CENTER') }}: {{ timeCenter }}
                </p>
              </div>
            </v-flex>

            <v-flex v-if="!readonly" md12 xs12>
              <div class="container">
                <div class="large-12 medium-12 small-12 cell">
                  <label>
                    {{ $t('NOTIFICATIONS.IMAGE') }}
                    <input
                      ref="file"
                      type="file"
                      accept="image/png, image/jpeg, image/jpg, image/gif, image/bmp"
                      @change="handleFileUpload()"
                    />
                  </label>
                </div>
              </div>
            </v-flex>

            <v-flex md12 xs12>
              <img v-if="fileSrc" :src="fileSrc" width="100" />

              <v-input v-else>{{
                $t('NOTIFICATIONS.IMAGE_IS_ABSENT')
              }}</v-input>
            </v-flex>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mask } from 'vue-the-mask';
import moment from 'moment-timezone';
import DecoupledDoc from '@ckeditor/ckeditor5-build-decoupled-document';
import '@ckeditor/ckeditor5-build-decoupled-document/build/translations/de';
import VCkeditor from '@/modules/_global/_components/VCkeditor';
import { Notification } from '@/modules/notifications/Notification';
import {
  notEmpty,
  textLine65,
  requiredTextLine65
} from '@/modules/_global/variables/rules.js';

export default {
  name: 'GeneralInformation',

  directives: {
    mask
  },

  components: {
    VCkeditor,
    AlertError: () => import('@/modules/_global/_components/AlertError')
  },

  model: {
    prop: 'itemOutside'
  },

  props: {
    itemOutside: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    localItem: null,
    error: null,
    valid: true,
    languages: null,
    dispatchTime: null,
    tab: null,
    rules: {
      notEmpty,
      textLine65,
      requiredTextLine65
    },
    file: '',
    fileSrc: null,
    showChooseTime: true,
    timeCenter: '',
    diffTimeZones: false,
    editor: DecoupledDoc
  }),

  computed: {
    editorConfig() {
      return {
        removePlugins: [
          'Image',
          'ImageCaption',
          'ImageStyle',
          'ImageToolbar',
          'ImageUpload',
          'MediaEmbed',
          'BlockQuote',
          'Table',
          'TableToolbar'
        ],
        toolbar: [
          'paragraph',
          '|',
          'fontfamily',
          'fontsize',
          'fontColor',
          'fontBackgroundColor',
          '|',
          'bold',
          'italic',
          'underline',
          'strikethrough',
          '|',
          'alignment',
          '|',
          'numberedList',
          'bulletedList',
          '|',
          'indent',
          'outdent',
          '|',
          'undo',
          'redo',
          '|',
          'link'
        ]
      };
    }
  },

  watch: {
    localItem: {
      deep: true,
      handler: 'updateItemOutside'
    }
  },

  created() {
    this.localItem = Object.assign({}, this.itemOutside);

    this.dispatchTime = this.localItem.dispatchTime;

    if (Notification.getTimezonesDiffInMinutes() != 0) {
      this.diffTimeZones = true;
    }

    if (this.localItem.dispatchTime) {
      this.showChooseTime = false;
      this.localItem.chooseTime = true;
      this.timeCenter = this.setTimeCenter(this.localItem.dispatchTime);
    }

    //image
    if (this.localItem.image) {
      this.fileSrc =
        process.env.VUE_APP_CORE_API + '/../storage/' + this.localItem.image;
    }

    // get languages
    this.$store
      .dispatch('localization/retrieveLanguages')
      .then(response => {
        this.languages = response.data;
      })
      .catch(error => {
        this.error = error.response;
      });
  },

  methods: {
    updateItemOutside() {
      this.$emit('input', this.localItem);

      if (!this.readonly) {
        this.timeCenter = this.setTimeCenter(this.localItem.dispatchTime);
      }
    },

    clearDispatchTime() {
      this.dispatchTime = null;
      this.localItem.dispatchTime = null;
    },

    validateDispatchTime() {
      if (this.localItem.chooseTime) {
        if (this.dispatchTime) {
          let isValidTime = moment(
            this.dispatchTime,
            'DD.MM.YYYY HH:mm',
            true
          ).isValid();

          let isTimeAfterNearestSending =
            moment(this.dispatchTime, 'DD.MM.YYYY HH:mm', true).diff(
              moment().add(
                this.$store.state.notification.cronFrequency,
                'minute'
              )
            ) > 0;

          if (!isValidTime || !isTimeAfterNearestSending) {
            this.localItem.dispatchTime = null;

            return this.$t('MESSAGE.INVALID_DATE_TIME');
          }
          this.localItem.dispatchTime = this.dispatchTime;

          return true;
        } else {
          this.localItem.dispatchTime = null;

          return this.$t('MESSAGE.THIS_FIELD_IS_REQUIRED');
        }
      } else {
        return true;
      }
    },

    handleFileUpload() {
      this.localItem.file = this.$refs.file.files[0];

      let reader = new FileReader();

      reader.addEventListener(
        'load',
        function() {
          this.fileSrc = reader.result;
        }.bind(this),
        false
      );

      reader.readAsDataURL(this.localItem.file);
    },

    setTimeCenter(datetime) {
      if (datetime) {
        return Notification.setTimeCenter(datetime).format('DD.MM.YYYY HH:mm');
      } else {
        return this.$t('MESSAGE.INVALID_DATE_TIME');
      }
    }
  }
};
</script>
